import { For, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { HistoryPoint } from '../lib/fleet-history';

// A line over time with one threshold mark (#6565, prototype `chart()`): the machine
// panel's CPU, Memory and Disk history. Dashboard-only, like `DataTable`: `packages/ui`
// holds cross-app primitives and refuses a one-consumer copy (`packages/ui/CLAUDE.md`
// § Boundary). `ChartWidget` is a CSS column chart with no lines, gaps or thresholds, so
// it cannot draw these series.
//
// GAPS ARE BREAKS. Each `segments` entry is one unbroken run of real points
// (`historySegments`, `lib/fleet-history.ts`), drawn as its own subpath; nothing joins
// two runs. A run of ONE point is a zero-length subpath, which the round line cap paints
// as a dot, so a lone reading between two gaps is still seen.
//
// The SVG is `role="img"` with its `summary` as the name ("CPU demand over the last 1
// hour, now 42%"); the axis labels beside it are decoration for a sighted reader.

const W = 480;
const H = 88;

export type LineChartProps = {
  /** Already translated: "CPU demand". */
  readonly label: string;
  /** Already translated: "Dashed line: tight at 75%". */
  readonly legend: string;
  /** Already translated: the accessible name. */
  readonly summary: string;
  readonly segments: readonly (readonly HistoryPoint[])[];
  /** The axis top, in percent. */
  readonly yMax: number;
  /** The dashed line, in percent. */
  readonly threshold: number;
  /** `danger` strokes the line in the destructive colour (an overloaded machine). */
  readonly tone?: 'default' | 'danger';
  /** Already translated: the left, middle and right x labels. */
  readonly xLabels: readonly [string, string, string];
};

const fmt = (n: number): string => n.toFixed(1);

/** One subpath per run: `M x y L x y …`; a single point repeats itself. */
export function linePath(segments: readonly (readonly HistoryPoint[])[]): string {
  return segments
    .map((run) => {
      const pts = run.length === 1 ? [run[0], run[0]] : run;
      return pts
        .map((p, i) => `${i === 0 ? 'M' : 'L'}${fmt((p?.x ?? 0) * W)} ${fmt(H - (p?.y ?? 0) * H)}`)
        .join(' ');
    })
    .join(' ');
}

export function LineChart(props: LineChartProps): JSX.Element {
  const thresholdY = (): number => H - (props.threshold / props.yMax) * H;
  const ticks = (): readonly { readonly value: number; readonly top: string }[] => [
    { value: props.yMax, top: '0%' },
    { value: props.threshold, top: `${(thresholdY() / H) * 100}%` },
    { value: 0, top: '100%' },
  ];
  return (
    <div class="line-chart" data-tone={props.tone ?? 'default'}>
      <div class="line-chart__top">
        <span class="line-chart__label">{props.label}</span>
        <span class="line-chart__legend">{props.legend}</span>
      </div>
      <div class="line-chart__plot">
        <svg
          class="line-chart__svg"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={props.summary}
        >
          <line
            class="line-chart__threshold"
            x1="0"
            x2={W}
            y1={fmt(thresholdY())}
            y2={fmt(thresholdY())}
            vector-effect="non-scaling-stroke"
          />
          <path
            class="line-chart__line"
            d={linePath(props.segments)}
            vector-effect="non-scaling-stroke"
          />
        </svg>
        <For each={ticks()}>
          {(tick) => (
            <span class="line-chart__y" style={{ top: tick.top }} aria-hidden="true">
              {t('fleet.panel.chart_pct', { value: String(tick.value) })}
            </span>
          )}
        </For>
      </div>
      <div class="line-chart__x" aria-hidden="true">
        <For each={props.xLabels}>{(label) => <span>{label}</span>}</For>
      </div>
    </div>
  );
}
